import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth } from "@/contexts/AuthContext";
import { formatDetail } from "@/services";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Swords, Mail, Lock, User } from "lucide-react";

export default function LoginPage() {
  const { t } = useTranslation();
  usePageTitle("Sign in");
  const { loginWithEmail, loginWithTwoFA, register, loginWithGoogle } =
    useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // 2FA challenge state — set when /auth/login returns requires_2fa.
  // Code-or-recovery toggle so the user can fall back to a recovery
  // code if they've lost their authenticator (KNOWN_ISSUES §1.49).
  const [twoFAChallenge, setTwoFAChallenge] = useState(null);
  const [twoFACode, setTwoFACode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  // Preserve invite code (?code=ABC) through auth so invite links work
  const destination = () => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    return code ? `/dashboard?code=${encodeURIComponent(code)}` : "/dashboard";
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const result = await loginWithEmail(email, password);
      if (result?.requires_2fa) {
        // Password OK but 2FA gate ahead. AuthContext intentionally
        // didn't set the user; show the challenge form here.
        setTwoFAChallenge(result.challenge_token);
        setTwoFACode("");
        setUseRecoveryCode(false);
      } else {
        navigate(destination());
      }
    } catch (err) {
      setError(
        formatDetail(err.response?.data?.detail) ||
          t("login.error.loginFailed"),
      );
    }
    setLoading(false);
  };

  const handleTwoFAVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await loginWithTwoFA(
        twoFAChallenge,
        useRecoveryCode ? { recoveryCode: twoFACode } : { code: twoFACode },
      );
      navigate(destination());
    } catch (err) {
      setError(
        formatDetail(err.response?.data?.detail) ||
          t("login.error.verificationFailed"),
      );
      // 401 means the challenge was consumed (or expired). Drop the
      // challenge state so the user starts the password step again.
      if (
        err.response?.status === 401 &&
        /challenge/i.test(formatDetail(err.response?.data?.detail))
      ) {
        setTwoFAChallenge(null);
      }
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    // Client-side display-name validation. Mirrors the backend's
    // `name: Field(min_length=1, max_length=50)` (KNOWN_ISSUES §1.21)
    // so whitespace-only / oversized entries fail inline instead of
    // round-tripping through a 422 toast.
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError(t("login.error.displayNameBlank"));
      return;
    }
    if (trimmed.length > 50) {
      setError(t("login.error.displayNameTooLong"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      await register(email, password, trimmed);
      navigate(destination());
    } catch (err) {
      setError(
        formatDetail(err.response?.data?.detail) ||
          t("login.error.registerFailed"),
      );
    }
    setLoading(false);
  };

  // 2FA challenge view — replaces the email/password form once
  // `requires_2fa` flips. The user is mid-login: password's been
  // accepted, just needs the second factor.
  if (twoFAChallenge) {
    return (
      <div
        className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] flex items-center justify-center p-4"
        id="main-content"
        role="main"
        tabIndex={-1}
        data-testid="login-page"
      >
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <div className="inline-flex items-center gap-3 mb-3">
              <Swords className="w-10 h-10 text-[#38B6FF]" />
              <h1
                className="text-4xl sm:text-5xl font-bold text-[#1A237E] dark:text-[#E3F2FD] tracking-tight"
                style={{ fontFamily: "Fredoka" }}
              >
                {t("login.title")}
              </h1>
            </div>
            <p className="text-base text-[#1A237E]/60 dark:text-[#E3F2FD]/60">
              {t("login.twoFA.subtitle")}
            </p>
          </div>
          <div
            className="p-8 border-2 shadow-xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-white/40 rounded-3xl"
            data-testid="two-fa-challenge"
          >
            <form onSubmit={handleTwoFAVerify} className="space-y-3">
              <p className="text-sm text-[#1A237E]/70 dark:text-[#E3F2FD]/70 mb-2">
                {useRecoveryCode
                  ? t("login.twoFA.recoveryInstructions")
                  : t("login.twoFA.instructions")}
              </p>
              <Input
                type="text"
                inputMode={useRecoveryCode ? "text" : "numeric"}
                autoComplete="one-time-code"
                placeholder={
                  useRecoveryCode
                    ? t("login.twoFA.recoveryCode")
                    : t("login.twoFA.placeholder")
                }
                aria-label={
                  useRecoveryCode
                    ? t("login.twoFA.recoveryCode")
                    : t("login.twoFA.code")
                }
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value)}
                className="bg-white/60 dark:bg-slate-900/60 border-[#1A237E]/10 dark:border-[#E3F2FD]/10 rounded-xl h-11 tracking-widest text-center font-bold text-lg"
                data-testid="two-fa-code-input"
                required
                maxLength={useRecoveryCode ? 40 : 10}
                autoFocus
              />
              <Button
                type="submit"
                disabled={loading || !twoFACode.trim()}
                className="w-full h-12 rounded-full bg-[#38B6FF] hover:bg-[#2196F3] text-white font-bold shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px] transition-all active:scale-95"
                data-testid="two-fa-submit-btn"
              >
                {loading ? t("common.loading") : t("login.button.verify")}
              </Button>
              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => {
                    setUseRecoveryCode(!useRecoveryCode);
                    setTwoFACode("");
                    setError("");
                  }}
                  className="text-[#1A237E]/60 dark:text-[#E3F2FD]/60 hover:text-[#1A237E] underline"
                  data-testid="two-fa-toggle-recovery"
                >
                  {useRecoveryCode
                    ? t("login.button.useAuthenticator")
                    : t("login.button.useRecovery")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTwoFAChallenge(null);
                    setTwoFACode("");
                    setError("");
                  }}
                  className="text-[#1A237E]/60 dark:text-[#E3F2FD]/60 hover:text-[#1A237E] underline"
                  data-testid="two-fa-cancel-btn"
                >
                  {t("login.button.cancel")}
                </button>
              </div>
            </form>
            {error && (
              <p
                className="mt-3 text-sm text-center text-red-500"
                data-testid="auth-error"
              >
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] flex items-center justify-center p-4"
      id="main-content"
      role="main"
      tabIndex={-1}
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-3 mb-3">
            <Swords className="w-10 h-10 text-[#38B6FF]" />
            <h1
              className="text-4xl sm:text-5xl font-bold text-[#1A237E] dark:text-[#E3F2FD] tracking-tight"
              style={{ fontFamily: "Fredoka" }}
            >
              {t("login.title")}
            </h1>
          </div>
          <p className="text-base text-[#1A237E]/60 dark:text-[#E3F2FD]/60">
            {t("login.tagline")}
          </p>
        </div>

        <div className="p-8 border-2 shadow-xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-white/40 rounded-3xl">
          {/* Google Auth — GIS ID-token flow via @react-oauth/google */}
          <div className="flex justify-center mb-6" data-testid="google-login-btn">
            <GoogleLogin
              onSuccess={async (cred) => {
                setError("");
                try {
                  await loginWithGoogle(cred.credential);
                  navigate(destination());
                } catch (err) {
                  setError(
                    formatDetail(err.response?.data?.detail) ||
                      t("login.error.googleSignInFailed"),
                  );
                }
              }}
              onError={() =>
                setError(t("login.error.googleSignInFailed"))
              }
              useOneTap={false}
              width="320"
            />
          </div>

          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-[#1A237E]/10 dark:bg-[#E3F2FD]/10" />
            <span className="text-xs text-[#1A237E]/40 dark:text-[#E3F2FD]/40 font-semibold">
              {t("login.or")}
            </span>
            <div className="flex-1 h-px bg-[#1A237E]/10 dark:bg-[#E3F2FD]/10" />
          </div>

          {/* Email/Password */}
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 mb-4 bg-[#E3F2FD] dark:bg-[#0a1428]/50 rounded-xl">
              <TabsTrigger
                value="login"
                className="text-sm font-semibold rounded-lg"
                data-testid="login-tab"
              >
                {t("login.tab.signIn")}
              </TabsTrigger>
              <TabsTrigger
                value="register"
                className="text-sm font-semibold rounded-lg"
                data-testid="register-tab"
              >
                {t("login.tab.signUp")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-4 h-4 text-[#1A237E]/30 dark:text-[#E3F2FD]/30" />
                  <Input
                    data-testid="login-email"
                    type="email"
                    placeholder={t("login.field.email")}
                    aria-label={t("login.field.email")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-white/60 dark:bg-slate-900/60 border-[#1A237E]/10 dark:border-[#E3F2FD]/10 rounded-xl h-11"
                    required
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-4 h-4 text-[#1A237E]/30 dark:text-[#E3F2FD]/30" />
                  <Input
                    data-testid="login-password"
                    type="password"
                    placeholder={t("login.field.password")}
                    aria-label={t("login.field.password")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-white/60 dark:bg-slate-900/60 border-[#1A237E]/10 dark:border-[#E3F2FD]/10 rounded-xl h-11"
                    required
                  />
                </div>
                <Button
                  data-testid="login-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-full bg-[#38B6FF] hover:bg-[#2196F3] text-white font-bold shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px] transition-all active:scale-95"
                >
                  {loading ? t("common.loading") : t("login.button.signIn")}
                </Button>
                <div className="text-right">
                  <Link
                    to="/forgot-password"
                    data-testid="forgot-password-link"
                    className="text-xs text-[#1A237E]/60 dark:text-[#E3F2FD]/60 hover:text-[#1A237E] dark:text-[#E3F2FD] underline"
                  >
                    {t("login.link.forgotPassword")}
                  </Link>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-3">
                <div className="relative">
                  <User className="absolute left-3 top-3 w-4 h-4 text-[#1A237E]/30 dark:text-[#E3F2FD]/30" />
                  <Input
                    data-testid="register-name"
                    placeholder={t("login.field.displayName")}
                    aria-label={t("login.field.displayName")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 bg-white/60 dark:bg-slate-900/60 border-[#1A237E]/10 dark:border-[#E3F2FD]/10 rounded-xl h-11"
                    required
                    minLength={1}
                    maxLength={50}
                    autoComplete="nickname"
                  />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-4 h-4 text-[#1A237E]/30 dark:text-[#E3F2FD]/30" />
                  <Input
                    data-testid="register-email"
                    type="email"
                    placeholder={t("login.field.email")}
                    aria-label={t("login.field.email")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-white/60 dark:bg-slate-900/60 border-[#1A237E]/10 dark:border-[#E3F2FD]/10 rounded-xl h-11"
                    required
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-4 h-4 text-[#1A237E]/30 dark:text-[#E3F2FD]/30" />
                  <Input
                    data-testid="register-password"
                    type="password"
                    placeholder={t("login.field.passwordMinChars")}
                    aria-label={t("login.field.passwordMinChars")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-white/60 dark:bg-slate-900/60 border-[#1A237E]/10 dark:border-[#E3F2FD]/10 rounded-xl h-11"
                    required
                    minLength={8}
                  />
                </div>
                <Button
                  data-testid="register-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-full bg-[#00E676] hover:bg-[#00C853] text-white font-bold shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px] transition-all active:scale-95"
                >
                  {loading
                    ? t("login.button.signingUp")
                    : t("login.button.signUp")}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          {error && (
            <p
              className="mt-3 text-sm text-center text-red-500"
              data-testid="auth-error"
            >
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
